'use client';

import { Box, Flex, freddyTheme, Typography } from '@coolblue-development/freddy';
import addToCartTrigger from '@coolblue-development/ga4-events/triggers/OrderBlock/addToCartTrigger';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { SquareProductImage } from '@coolblue-development/next-patterns/client';
import { ATCPostalCodeCheckerModal } from '@coolblue-development/next-postal-code/client';
import { getPostalCode } from '@coolblue-development/next-postal-code/server';
import {
  SalesPrice,
  SalesPriceCurrent,
  SalesPriceLabel
} from '@coolblue-development/next-sales-price/client';
import { AddToCartButton } from '@coolblue-development/next-shopping-cart/client';
import { Product } from '@features/product/typings';
import { useBreakpoints } from '@hooks/useBreakpoints';
import { useTranslation } from '@i18n/useTranslation';
import { addProductToShoppingCart } from '@utils/nextShoppingCartActions/addProductToShoppingCart';
import { RefObject, useRef, useState } from 'react';

export const SubscriptionInformationProductBlock = ({
  productData,
  shouldDisableAddToCart,
  isPostalCodeCheckerImprovementSplitTestInVariation,
  loginUrl
}: {
  productData: Product;
  shouldDisableAddToCart: boolean;
  isPostalCodeCheckerImprovementSplitTestInVariation: boolean;
  loginUrl: string;
}) => {
  const { trackEvent } = useAnalytics();
  const { t } = useTranslation('webshop');
  const [isLoading, setIsLoading] = useState(false);
  const [isPostalCodeCheckerOpen, setIsPostalCodeCheckerOpen] = useState(false);

  const { isMobile } = useBreakpoints();

  const trackingEvent = () => {
    trackEvent(
      addToCartTrigger({
        item_id: productData.id,
        order_type: 'purchase'
      })
    );
  };

  const onSubmit = async (event: { preventDefault: () => void }) => {
    event.preventDefault();
    setIsLoading(true);

    if (isPostalCodeCheckerImprovementSplitTestInVariation) {
      if (!(await getPostalCode())) {
        setIsLoading(false);
        setIsPostalCodeCheckerOpen(true);
        return;
      }
    }

    await addProductToShoppingCart({
      productId: productData.id,
      params: {
        subscriptionProductId: productData.id
      },
      revalidate: false
    }).then(response => {
      setIsLoading(false);
      const redirectUrl = response?.data?.redirectUrl;

      if (redirectUrl) {
        window.location.assign(redirectUrl);
      }
    });
  };

  const formRef = useRef<HTMLFormElement>(null);
  const installmentAmount = productData.subscription?.installmentAmount ?? null;
  const oneTimePaymentAmount = productData.subscription?.oneTimePaymentAmount ?? null;

  if (isMobile) {
    return (
      <>
        <Flex
          direction='column'
          alignItems='center'
          gap={400}
          p={400}
          sx={{
            border: `1px solid ${freddyTheme.color.border.base}`
          }}
        >
          <Flex gap={200} alignItems='center'>
            <SquareProductImage
              alt='' // As the product name is displayed, the alt attribute is redundant here.
              imageId={productData.primaryImage.id}
              loading='lazy'
            />

            <Box flex={1}>
              <Typography variant='h3' mb={200}>
                {productData.name} {t('subscriptionInformation.subscription')}
              </Typography>

              {installmentAmount && oneTimePaymentAmount && (
                <SalesPrice
                  variant='subscription'
                  currentPrice={<SalesPriceCurrent price={installmentAmount} />}
                  additionalLabel={
                    <SalesPriceLabel price={oneTimePaymentAmount} type='oneTimePayment' />
                  }
                />
              )}
            </Box>
          </Flex>
          <form onSubmit={onSubmit} ref={formRef}>
            <AddToCartButton
              isFullWidth
              label={t('subscriptionInformation.addToCart')}
              ariaLabel={t('subscriptionInformation.addToCart')}
              disabled={shouldDisableAddToCart}
              onClick={async () => {}}
              onSuccess={() => trackingEvent()}
              loading={isLoading}
            />
          </form>
        </Flex>
        <ATCPostalCodeCheckerModal
          productType={productData.productType.singularName}
          loginUrl={loginUrl}
          isOpen={isPostalCodeCheckerOpen}
          setIsOpen={setIsPostalCodeCheckerOpen}
          formRef={formRef as RefObject<HTMLFormElement>}
        />
      </>
    );
  }

  return (
    <>
      <Flex
        justifyContent='space-between'
        alignItems='center'
        p={400}
        sx={{
          border: `1px solid ${freddyTheme.color.border.base}`
        }}
      >
        <Flex gap={200} alignItems='center'>
          <SquareProductImage
            alt='' // As the product name is displayed, the alt attribute is redundant here.
            imageId={productData.primaryImage.id}
            loading='lazy'
          />

          <Box flex={1}>
            <Typography variant='h3' mb={200}>
              {productData.name} {t('subscriptionInformation.subscription')}
            </Typography>

            {installmentAmount && oneTimePaymentAmount && (
              <SalesPrice
                variant='subscription'
                currentPrice={<SalesPriceCurrent price={installmentAmount} />}
                additionalLabel={
                  <SalesPriceLabel price={oneTimePaymentAmount} type='oneTimePayment' />
                }
              />
            )}
          </Box>
        </Flex>
        <form onSubmit={onSubmit} ref={formRef}>
          <AddToCartButton
            label={t('subscriptionInformation.addToCart')}
            ariaLabel={t('subscriptionInformation.addToCart')}
            disabled={shouldDisableAddToCart}
            onSuccess={() => trackingEvent()}
            loading={isLoading}
          />
        </form>
      </Flex>
      <ATCPostalCodeCheckerModal
        productType={productData.productType.singularName}
        loginUrl={loginUrl}
        isOpen={isPostalCodeCheckerOpen}
        setIsOpen={setIsPostalCodeCheckerOpen}
        formRef={formRef as RefObject<HTMLFormElement>}
      />
    </>
  );
};
